<template>
  <div v-loading="pageLoading" class="home-page" element-loading-text="加载中...">
    <div class="shell">
      <div class="shell-inner">
        <div class="canvas" :style="canvasStyle">
          <div class="container">
            <div class="content-layout">
              <div ref="mainContent" class="main">
                <div class="first-screen" :style="firstScreenStyle">
                  <CompanyIntro :site-info="siteTxtInfo" />
                  <ShortcutBoard :active-key="activeShortcutKey" :is-logged-in="isLoggedIn"
                    @shortcut-click="handleShortcutClick" @logout="handleLogout" />
                </div>
                <RankingBoard />
              </div>

              <SidebarStage :stage-height="sidebarStageHeight" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <AgreementDialog :visible="isAgreementDialogVisible" :dialog-key="activeDialog" @close="closeDialog" />
    <ContactDialog :visible="activeDialog === 'contact'" :content-html="siteTxtInfo.contact_content"
      @close="closeDialog" />
    <IcpDialog :visible="activeDialog === 'icp-record'" :icp-number="siteTxtInfo.beian" @close="closeDialog" />
    <LoginDialog :visible="activeDialog === 'login'" @close="closeDialog" @login-success="handleLoginSuccess" />
    <LicenseDialog :visible="activeDialog === 'license'" :image-src="siteTxtInfo.business_license" title="营业执照"
      @close="closeDialog" />
    <LicenseDialog :visible="activeDialog === 'icp-license'" :image-src="siteTxtInfo.icp_license" image-alt="ICP许可证"
      title="ICP许可证" @close="closeDialog" />
    <LicenseDialog :visible="activeDialog === 'honor'" :image-list="honorImageList" image-alt="荣誉证书" title="荣誉证书"
      @close="closeDialog" />
    <QrShortcutDialog :visible="activeDialog === 'qr'" :qr-image="siteTxtInfo.payment_qr" @close="closeDialog"
      @view-fee="handleViewFeeStandard" />
    <ServiceTimeDialog :visible="activeDialog === 'service'" :service-time="siteTxtInfo.service_time"
      :kefu-phone="siteTxtInfo.kefu_phone" @close="closeDialog" />
    <SearchDialog :visible="activeDialog === 'search'" :pay-info="searchPayInfo"
      :pay-info-query-key="searchPayInfoQueryKey" :submitting="searchSubmitting" @close="closeDialog('search')"
      @submit="handleSearchDialogSubmit" @pay="handleSearchDialogPay" />
    <SearchPaymentDialog :visible="activeDialog === 'search-payment'" :qr-image="siteTxtInfo.payment_qr"
      :amount="searchPayInfo ? searchPayInfo.totalPrice : ''" :submitting="paymentSubmitting"
      @close="handleSearchPaymentClose" @pay-success="handleSearchPaymentSuccess" @view-fee="handleViewFeeStandard" />
    <SearchPaymentSuccessDialog :visible="activeDialog === 'search-success'"
      @close="closeDialog('search-success', 'search')" @goto-orders="handleGotoOrders" />
  </div>
</template>

<script>
import { getToken, isLoggedIn } from "@/util/token-util.js";
import bus from "@/util/bus.js";
import AgreementDialog from "./components/AgreementDialog.vue";
import CompanyIntro from "./components/CompanyIntro.vue";
import ContactDialog from "./components/ContactDialog.vue";
import LicenseDialog from "./components/LicenseDialog.vue";
import LoginDialog from "./components/LoginDialog.vue";
import QrShortcutDialog from "./components/QrShortcutDialog.vue";
import RankingBoard from "./components/RankingBoard.vue";
import SearchDialog from "./components/SearchDialog.vue";
import SearchPaymentDialog from "./components/SearchPaymentDialog.vue";
import SearchPaymentSuccessDialog from "./components/SearchPaymentSuccessDialog.vue";
import ServiceTimeDialog from "./components/ServiceTimeDialog.vue";
import ShortcutBoard from "./components/ShortcutBoard.vue";
import SidebarStage from "./components/SidebarStage.vue";
import IcpDialog from "./components/IcpDialog.vue";

const AGREEMENT_DIALOG_KEYS = ["agreement", "fee", "privacy"];

const SHORTCUT_ROUTE_MAP = {
  message: "/message-board",
  feedback: "/feedback",
  order: "/my-orders",
};

const HOME_CANVAS_WIDTH = 1918;

function buildSearchPayload(formData = {}) {
  return {
    keyword: formData.keyword || "",
    province: formData.province || "",
    startDate: formData.startDate || "",
    endDate: formData.endDate || "",
    quantity: String(formData.quantity || ""),
  };
}

function buildSearchPayInfoQueryKey(payload = {}) {
  return JSON.stringify({
    keyword: payload.keyword || "",
    province: payload.province || "",
    startDate: payload.startDate || "",
    endDate: payload.endDate || "",
    quantity: String(payload.quantity || ""),
  });
}

export default {
  name: "HomeConfirmPage",
  components: {
    AgreementDialog,
    CompanyIntro,
    ContactDialog,
    IcpDialog,
    LicenseDialog,
    LoginDialog,
    QrShortcutDialog,
    RankingBoard,
    SearchDialog,
    SearchPaymentDialog,
    SearchPaymentSuccessDialog,
    ServiceTimeDialog,
    ShortcutBoard,
    SidebarStage,
  },
  created() {
    this.fetchSiteTxtInfo();
    bus.$on("openLoginDialog", this.handleOpenLoginDialog);
  },
  data() {
    return {
      activeShortcutKey: "",
      activeDialog: "",
      pageScale: 1,
      mainContentResizeObserver: null,
      siteInfoLoading: false,
      searchSubmitting: false,
      paymentSubmitting: false,
      searchPayInfo: null,
      searchPayInfoQueryKey: "",
      pendingOrderPayload: null,
      sidebarStageHeight: 0,
      siteTxtInfo: {},
      isLoggedIn: !!getToken(),
      viewportHeight: 0,
    };
  },
  computed: {
    canvasStyle() {
      return {
        zoom: this.pageScale,
      };
    },
    firstScreenMinHeight() {
      if (!this.pageScale || !this.viewportHeight) {
        return 0;
      }
      return Math.max(0, this.viewportHeight / this.pageScale - 28);
    },
    firstScreenStyle() {
      const minHeight = this.firstScreenMinHeight;
      return minHeight ? { minHeight: minHeight + "px" } : null;
    },
    pageLoading() {
      return this.siteInfoLoading;
    },
    isAgreementDialogVisible() {
      return AGREEMENT_DIALOG_KEYS.includes(this.activeDialog);
    },
    honorImageList() {
      return Array.isArray(this.siteTxtInfo.honor_list)
        ? this.siteTxtInfo.honor_list
        : [];
    },
  },
  mounted() {
    this.toggleHomeViewportClass(true);
    this.updatePageScale();
    this.viewportHeight = window.innerHeight;
    this.initSidebarStageHeightObserver();
    this.updateSidebarStageHeight();

    window.addEventListener("resize", this.handleWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleWindowResize);
    this.destroySidebarStageHeightObserver();
    this.toggleHomeViewportClass(false);
    bus.$off("openLoginDialog", this.handleOpenLoginDialog);
  },
  methods: {
    handleOpenLoginDialog(message) {
      this.activeDialog = "login";
      this.$message.warning(message || "请登录");
    },
    async fetchSiteTxtInfo() {
      this.siteInfoLoading = true;

      try {
        const res = await this.$api({
          url: "Txt/show",
          method: "get",
          data: {
            id: 107,
          },
        });

        if (res.code === 200 && res.data && res.data.txts) {
          this.siteTxtInfo = res.data.txts;
        }
      } catch (error) {
        console.log("fetchSiteTxtInfo error", error);
      } finally {
        this.siteInfoLoading = false;
        this.updateSidebarStageHeight();
      }
    },
    resetSearchState() {
      this.searchPayInfo = null;
      this.searchPayInfoQueryKey = "";
      this.pendingOrderPayload = null;
    },
    ensureLoggedIn(message = "请先登录后再操作") {
      if (isLoggedIn()) {
        return true;
      }

      this.activeDialog = "login";
      this.$message.warning(message);
      return false;
    },
    toggleHomeViewportClass(shouldAdd) {
      const action = shouldAdd ? "add" : "remove";

      document.documentElement.classList[action]("home-confirm-fit");
      document.body.classList[action]("home-confirm-fit");
    },
    handleWindowResize() {
      this.updatePageScale();
      this.viewportHeight = window.innerHeight;
      this.updateSidebarStageHeight();
    },
    updatePageScale() {
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth;
      const nextScale = viewportWidth / HOME_CANVAS_WIDTH;

      if (nextScale > 0 && nextScale !== this.pageScale) {
        this.pageScale = nextScale;
      }
    },
    initSidebarStageHeightObserver() {
      if (typeof ResizeObserver === "undefined") {
        return;
      }

      this.$nextTick(() => {
        const mainContent = this.$refs.mainContent;

        if (!mainContent) {
          return;
        }

        this.destroySidebarStageHeightObserver();
        this.mainContentResizeObserver = new ResizeObserver(() => {
          this.updateSidebarStageHeight();
        });
        this.mainContentResizeObserver.observe(mainContent);
      });
    },
    destroySidebarStageHeightObserver() {
      if (!this.mainContentResizeObserver) {
        return;
      }

      this.mainContentResizeObserver.disconnect();
      this.mainContentResizeObserver = null;
    },
    updateSidebarStageHeight() {
      this.$nextTick(() => {
        const mainContent = this.$refs.mainContent;
        const nextHeight = mainContent
          ? Math.ceil(mainContent.offsetHeight)
          : 0;

        if (nextHeight !== this.sidebarStageHeight) {
          this.sidebarStageHeight = nextHeight;
        }
      });
    },
    clearActiveShortcutKeyIfMatches(key) {
      if (this.activeShortcutKey === key) {
        this.activeShortcutKey = "";
      }
    },
    closeDialog(dialogKey = this.activeDialog, activeKey = dialogKey) {
      if (dialogKey === "search") {
        this.resetSearchState();
      }

      if (this.activeDialog === dialogKey) {
        this.activeDialog = "";
      }

      if (dialogKey === "login") {
        this.isLoggedIn = !!getToken();
      }

      this.clearActiveShortcutKeyIfMatches(activeKey);
    },
    handleSearchDialogPay() {
      if (!isLoggedIn()) {
        bus.$emit("openLoginDialog", "请登录后再支付");
        return;
      }

      if (!this.searchPayInfo || !this.pendingOrderPayload) {
        this.$message.warning("请先搜索并确认价格信息");
        return;
      }

      this.handleSearchPaymentSuccess();
    },
    handleShortcutClick(key) {
      this.activeShortcutKey = key;

      const routePath = SHORTCUT_ROUTE_MAP[key];
      if (routePath) {
        this.gotoRoute(routePath);
        return;
      }

      if (key === "search") {
        this.resetSearchState();
      }

      this.activeDialog = key;
    },
    async handleSearchDialogSubmit(formData) {
      const quantity = Number(formData.quantity || 0);

      if (!quantity || quantity < 1) {
        this.$message.warning("请输入正确的购买数量");
        return;
      }

      this.searchSubmitting = true;

      try {
        const payload = buildSearchPayload(formData);
        const res = await this.$api({
          url: "CaseCause/getPayInfo",
          method: "post",
          data: payload,
        });

        if (res.code === 200 && res.data) {
          this.pendingOrderPayload = payload;
          this.searchPayInfo = res.data;
          this.searchPayInfoQueryKey = buildSearchPayInfoQueryKey(payload);
          this.$message.success("价格信息已生成，请确认后支付");
          return;
        }

        this.$message.warning(res.msg || "当前筛选条件暂无可购买数据");
      } catch (error) {
        this.$message.error("预下单失败，请稍后重试");
      } finally {
        this.searchSubmitting = false;
      }
    },
    async handleSearchPaymentSuccess() {
      if (!this.pendingOrderPayload) {
        this.$message.warning("请先填写检索条件");
        return;
      }

      if (!this.ensureLoggedIn("请先登录后再下单")) {
        return;
      }

      this.paymentSubmitting = true;

      try {
        const res = await this.$api({
          url: "CaseCause/createOrder",
          method: "post",
          data: this.pendingOrderPayload,
        });

        if (res.code === 200) {
          this.activeDialog = "search-payment";
          this.$message.success(res.msg || "订单创建成功");
          return;
        }

        this.$message.error(res.msg || "下单失败");
      } catch (error) {
        this.$message.error("下单失败，请稍后重试");
      } finally {
        this.paymentSubmitting = false;
      }
    },
    handleSearchPaymentClose() {
      this.closeDialog('search-payment', 'search');
      this.gotoRoute("/my-orders");
    },
    handleViewFeeStandard() {
      this.activeShortcutKey = "fee";
      this.activeDialog = "fee";
    },
    handleLoginSuccess() {
      this.isLoggedIn = !!getToken();
    },
    handleLogout() {
      this.isLoggedIn = false;
      this.$message.success("已退出登录");
    },
    handleGotoOrders() {
      this.gotoRoute("/my-orders");
    },
    gotoRoute(path) {
      this.activeDialog = "";
      this.activeShortcutKey = "";
      this.$router.push(path);
    },
  },
};
</script>

<style scoped>
.home-page {
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  background: #ffffff;
}

.shell {
  width: 100%;
}

.shell-inner {
  display: flex;
  justify-content: flex-start;
  width: 100%;
}

.canvas {
  flex: 0 0 auto;
  width: 1918px;
  padding: 28px 0 80px;
  transform-origin: top left;
}

.container {
  width: 1918px;
}

.content-layout {
  display: grid;
  grid-template-columns: 1190px 720px;
  column-gap: 8px;
  align-items: stretch;
}

.main {
  width: 1190px;
  padding-right: 0;
  align-self: start;
}

.first-screen {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
</style>
